import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { SiteGateProvider } from "@/context/SiteGateContext";
import SiteGateGuard from "@/components/SiteGateGuard";

// Pages
import Home from "@/pages/Home";
import AcheterPage from "@/pages/AcheterPage";
import LouerPage from "@/pages/LouerPage";
import PropertyDetail from "@/pages/PropertyDetail";
import OffMarket from "@/pages/OffMarket";
import Contact from "@/pages/Contact";
import ValuationPage from "@/pages/ValuationPage";
import RechercherPage from "@/pages/RechercherPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProperties from "@/pages/admin/AdminProperties";
import AdminLeads, { AdminLeadDetail } from "@/pages/admin/AdminLeads";
import AdminPropertyForm from "@/pages/admin/AdminPropertyForm";
import AdminSiteAccess from "@/pages/admin/AdminSiteAccess";

// Components
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import CookieBanner from "@/components/CookieBanner";

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <>
    <Navigation />
    {children}
    <Footer />
    <CookieBanner />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AdminAuthProvider>
          <SiteGateProvider>
            <div className="App">
              <BrowserRouter unstable_useTransitions={false}>
                <ScrollToTop />
                <SiteGateGuard>
                  <Routes>
                    {/* Admin Routes - No Navigation/Footer */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/properties" element={<AdminProperties />} />
                    <Route path="/admin/properties/new" element={<AdminPropertyForm />} />
                    <Route path="/admin/properties/:propertyId/edit" element={<AdminPropertyForm />} />
                    <Route path="/admin/leads" element={<AdminLeads />} />
                    <Route path="/admin/leads/:leadId" element={<AdminLeadDetail />} />
                    <Route path="/admin/site-access" element={<AdminSiteAccess />} />
                    <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

                    {/* Public Routes - With Navigation/Footer */}
                    <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                    <Route path="/acheter" element={<PublicLayout><AcheterPage /></PublicLayout>} />
                    <Route path="/louer" element={<PublicLayout><LouerPage /></PublicLayout>} />
                    <Route path="/buy" element={<Navigate to="/acheter" replace />} />
                    <Route path="/rent" element={<Navigate to="/louer" replace />} />
                    <Route path="/properties" element={<Navigate to="/acheter" replace />} />
                    <Route path="/properties/:id" element={<PublicLayout><PropertyDetail /></PublicLayout>} />
                    <Route path="/off-market" element={<PublicLayout><OffMarket /></PublicLayout>} />
                    <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                    <Route path="/estimation" element={<PublicLayout><ValuationPage /></PublicLayout>} />
                    <Route path="/rechercher" element={<PublicLayout><RechercherPage /></PublicLayout>} />
                    <Route path="/search" element={<Navigate to="/rechercher" replace />} />
                    <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                    <Route path="/cookies" element={<PublicLayout><CookiePolicy /></PublicLayout>} />
                    <Route path="/about" element={<Navigate to="/" replace />} />
                    <Route path="/services" element={<Navigate to="/" replace />} />
                  </Routes>
                </SiteGateGuard>
                <Toaster position="bottom-right" />
              </BrowserRouter>
            </div>
          </SiteGateProvider>
        </AdminAuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
